import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../Header/Header.js'
import Handsontable from "handsontable";
import { HotTable } from '@handsontable/react-wrapper';
import { getData, deleteData, putData } from '../../FetchApiServices.js';
import Loader from '../Assets/Loader.js';
import ConfirmationModal from '../Assets/ConfirmationModal.js';


const ViewUsers = () => {
    const [users, setUsers] = useState([]);
    const [loader, setLoader] = useState(false);
    let navigate = useNavigate();
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    const fetchData = useCallback(async () => {
        setLoader(true);
        const response = await getData('users');
        if (response?.data?.data) {
            const osifUsers = response.data.data.filter((u) => !u.is_t2t);
            setUsers(osifUsers)
            generateSerialNo(osifUsers)
        }
        setLoader(false);
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);


    const generateSerialNo = (users) => {
        let serialNo = 1;
        return users?.map(user => {
            user.serial_no = serialNo;
            serialNo++;
            return user;
        });
    }


    const hotTableRef = useRef(null);

    // Custom renderer for action buttons
    const actionRenderer = (_instance, td, row, _col, _prop, _value, _cellProperties) => {
        Handsontable.dom.empty(td);

        // Create "Edit" button
        const editButton = document.createElement("i");
        editButton.innerHTML = "<span class='action-icons'>Edit</span>";
        editButton.className = "fa fa-pencil mx-2 ft-6 text-primary";
        editButton.style.cursor = "pointer";
        editButton.onclick = () => handleEdit(row);

        // Create "Delete" button
        const deleteButton = document.createElement("i");
        deleteButton.innerHTML = "<span class='action-icons'>Delete</span>";
        deleteButton.className = "fa fa-trash mx-2 ft-6 text-danger pe-auto";
        deleteButton.style.cursor = "pointer";
        deleteButton.onclick = () => handleDelete(row);

        // Append buttons to the cell
        td.appendChild(editButton);
        td.appendChild(deleteButton);

        return td;
    };

    // Handler for Downtime Permission Toggle
    const handleDowntimeToggle = async (userId, field, currentValue) => {
        const newValue = currentValue === 1 ? 0 : 1;

        // Optimistic UI Update: Update local state immediately
        const updatedUsers = users.map(user => {
            if (user.user_id === userId) {
                return { ...user, [field]: newValue };
            }
            return user;
        });
        setUsers(updatedUsers);

        // API Call
        try {
            // Using putData to send JSON payload
            // Payload example: { "add_downtime": 1 }
            const response = await putData(`users/${userId}`, { [field]: newValue });

            if (response && response.error) {
                console.error("Failed to update downtime permission", response.error);
                // On error, revert the change by re-fetching data
                fetchData();
            }
        } catch (error) {
            console.error("API Error updating downtime:", error);
            fetchData(); // Revert on error
        }
    };

    // Custom Renderer for Downtime Columns (Switches)
    const downtimeRenderer = (instance, td, row, _col, _prop, _value, _cellProperties) => {
        Handsontable.dom.empty(td);

        // Get the actual user object safely, handling sorting/filtering
        const physicalRow = instance.toPhysicalRow(row);
        const userData = users[physicalRow];

        if (!userData) return td;

        const container = document.createElement('div');
        container.className = "d-flex justify-content-center align-items-center";
        container.style.gap = "10px";

        const createSwitch = (label, field) => {
            const wrapper = document.createElement('div');
            wrapper.className = "form-check form-switch m-0 d-flex align-items-center";

            const input = document.createElement('input');
            input.className = "form-check-input";
            input.type = "checkbox";
            input.role = "switch";
            input.checked = userData[field] === 1;
            input.style.cursor = "pointer";

            // Event listeners
            input.onclick = (e) => e.stopPropagation(); // Prevent row selection logic from interfering
            input.onchange = () => handleDowntimeToggle(userData.user_id, field, userData[field]);

            const lbl = document.createElement('label');
            lbl.className = "form-check-label text-nowrap";
            lbl.innerText = label;
            lbl.style.fontSize = "12px";
            lbl.style.marginLeft = "5px";
            lbl.style.cursor = "pointer";
            // Allow clicking label to toggle switch
            lbl.onclick = (e) => {
                e.stopPropagation();
                input.click();
            };

            wrapper.appendChild(input);
            wrapper.appendChild(lbl);
            return wrapper;
        };

        // Add the 3 switches

        container.appendChild(createSwitch("View", "view_downtime"));
        container.appendChild(createSwitch("Add", "add_downtime"));
        container.appendChild(createSwitch("Edit", "edit_downtime"));

        td.appendChild(container);
        td.className = "htMiddle htCenter"; // Center content
        return td;
    };


    const handleEdit = (row) => {
        if (hotTableRef.current?.hotInstance) {
            // Updated to use Physical Row for safety (works even if columns are reordered)
            const physicalRow = hotTableRef.current.hotInstance.toPhysicalRow(row);
            const user = users[physicalRow];

            if (user) {
                navigate('/edit-user', { state: { id: user.user_id } });
            }
        } else {
            console.error("hotTableRef is not initialized");
        }
    };

    // Handle delete action
    const handleDelete = (row) => {
        if (hotTableRef.current?.hotInstance) {
            // Updated to use Physical Row for safety
            const physicalRow = hotTableRef.current.hotInstance.toPhysicalRow(row);
            const user = users[physicalRow];

            if (user) {
                setSelectedRow(user.user_id);
                setShowConfirmationModal(true);
            }
        } else {
            console.error("hotTableRef is not initialized");
        }
    };

    const confirmDelete = async () => {
        if (selectedRow) {
            const response = await deleteData(`users/${selectedRow}`);
            if (response) {
                fetchData();
            }
            setShowConfirmationModal(false);
            setSelectedRow(null);
        }
    };

    const button = <Link to="/add-user" type="button" className="btn-success btn btn-sm">Add User</Link>

    return (
        <>
            {loader && <Loader />}
            {showConfirmationModal && <ConfirmationModal showModal={showConfirmationModal} setShowModal={setShowConfirmationModal} title="Delete User" message="Are you sure you want to delete this user?" onConfirm={confirmDelete} />}
            <Header title="Members List" button={button} />
            <div className="container-fluid " >
                <div className="row justify-content-center" >
                    <div className="col-12 listing-view ">
                        <HotTable
                            ref={hotTableRef}
                            data={users}
                            className="htCenter"
                            columns={[
                                { data: 'serial_no', title: 'S.No.', type: 'text', readOnly: true, dropdownMenu: false, width: 60 },
                                { title: 'Employee ID', type: 'text', data: 'emp_id', width: 120 },
                                { title: 'Name', type: 'text', data: 'name', width: 150 },
                                { title: 'User Email', type: 'text', data: 'email', width: 200, className: 'notranslate text-break' },
                                { title: 'Phone', type: 'text', data: 'phone', width: 120 },
                                { title: 'Role', type: 'text', data: 'role', width: 120 },
                                { title: 'Company', type: 'text', data: 'company_name', width: 150 },
                                { title: 'Department', type: 'text', data: 'department_name', width: 120 },
                                { title: 'Location', type: 'text', data: 'location_name', width: 100 },
                                { title: 'Section', type: 'text', data: 'section_name', width: 120, className: 'notranslate' },
                                { title: 'Position', type: 'text', data: 'position', width: 120, className: 'notranslate' },
                                {
                                    title: 'Action',
                                    type: 'text',
                                    data: 'user_id',
                                    dropdownMenu: false,
                                    enableHtml: true,
                                    renderer: actionRenderer,
                                    width: 200
                                },
                                {
                                    title: ' Downtime / CAPA',
                                    data: 'user_id', // Binds to user_id, but renderer uses full row object
                                    dropdownMenu: false,
                                    renderer: downtimeRenderer,
                                    width: 280, // Width for 3 switches
                                    readOnly: true
                                }
                            ]}

                            filters={true}
                            dropdownMenu={true}
                            readOnly={true}
                            autoWrapRow={true}
                            autoWrapCol={true}
                            manualColumnResize={true}
                            manualColumnMove={true}
                            manualRowMove={true}
                            height={'calc(100vh - 90px)'}
                            licenseKey={process.env.REACT_APP_EXCEL_LICENSE_KEY}
                        />
                    </div>
                </div>

            </div>
        </>
    )
}

export default ViewUsers